import { Component, type ErrorInfo, type ReactNode } from 'react'
import { RotateCcw } from 'lucide-react'

import { Button } from '@/shared/ui/button'
import { BrandMark } from '@/shared/components/brand-mark'
import { logger } from '@upadelmatch/core'

/**
 * App-wide render error boundary.
 *
 * Audit 2026-08-06: the app had NO boundary anywhere — `main.tsx` rendered
 * `<App/>` bare and no route declared an `errorElement`. Any render throw left a
 * permanently blank page, and because the service worker precaches the shell,
 * reloading re-entered the identical broken state. A white screen you cannot
 * reload out of is the worst failure mode a PWA has, and it is invisible to
 * lint, typecheck and unit tests.
 *
 * The recovery offers a genuine escape rather than a bare "reload": `Reintentar`
 * remounts the subtree from the current URL, and `Volver al inicio` does a hard
 * `location.replace('/')` — which also drops whatever route state caused the
 * throw, the case a soft retry cannot fix.
 */
interface Props {
  children: ReactNode
}
interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // The shared logger scrubs PII before anything reaches Sentry.
    logger.e('[app] render crash', {
      message: error.message,
      componentStack: info.componentStack?.slice(0, 1200),
    })
  }

  override render() {
    if (!this.state.error) return this.props.children
    return (
      <main className="min-h-full flex flex-col items-center justify-center gap-5 px-6 text-center safe-top safe-bottom">
        <span className="text-[color:var(--brand-mint)]">
          <BrandMark size={56} />
        </span>
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[length:var(--fs-xl)] font-bold">Algo se ha roto por aquí</h1>
          <p className="text-[color:var(--fg-muted)] max-w-[38ch]">
            No es culpa tuya. Ya nos ha llegado el aviso — inténtalo otra vez o vuelve al inicio.
          </p>
        </div>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <Button size="lg" onClick={() => this.setState({ error: null })}>
            <RotateCcw size={18} /> Reintentar
          </Button>
          <Button variant="outline" size="lg" onClick={() => window.location.replace('/')}>
            Volver al inicio
          </Button>
        </div>
      </main>
    )
  }
}
